/**
 * Safely converts urls into path strings. Urls can start with https://
 *
 * @param url full url or path possibly starting with https:// which has to be removed
 */
export const parseUrl = (url: string): string => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const u = new URL(url);
      return u.pathname;
    } catch {
      return url;
    }
  }
  return url;
};

/**
 * Gets all queries from a URL and returns as dictionary.
 *
 * @param url full url or path possibly starting with https:// which has to be removed
 */
export const extractQuery = (
  url: string,
): {
  [key: string]: string | undefined;
} => {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return {};
  }
  try {
    const u = new URL(url);
    return Object.fromEntries(u.searchParams) as { [key: string]: string | undefined };
  } catch {
    return {};
  }
};

/**
 * Extract parameter from express path style syntax (e.g. /users/:id).
 *
 * @param url full url or path possibly starting with https://
 * @param matcher string in express path style syntax to indicate params (e.g. /horses/:id)
 */
export const extractParams = <T extends object = object>(url: string, matcher: string): T => {
  const path = parseUrl(url);
  const pathSegments = path.split('/').filter(Boolean);
  const matcherSegments = matcher.split('/').filter(Boolean);
  const params: Record<string, string> = {};
  for (let i = 0; i < matcherSegments.length; i++) {
    const segment = matcherSegments[i];
    if (segment!.startsWith(':')) {
      const paramName = segment!.slice(1);
      const value = pathSegments[i];
      params[paramName] = value !== undefined ? decodeURIComponent(value) : undefined!;
    }
  }
  return params as T;
};
